import { Injectable } from "@angular/core";
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { HttpService } from "services/http.service";
import { Response } from "services/models/response";
import { map, timeout } from "rxjs/operators";
import { InsightAbsenteismModel } from "services/models/corporate/insights/insightAbsenteism.model";
import { InsightOvertimeModel } from "services/models/corporate/insights/insightOvertime.model";
import { InsightSummaryModel } from "services/models/corporate/insights/insightSummary.model";
import { InsightHeadcountModel } from "services/models/corporate/insights/InsightHeadcount.model";
import { InsightTurnOverModel } from "services/models/corporate/insights/insightTurnover.model";
import { InsightVacationModel } from "services/models/corporate/insights/insightVacation.model";
import { InsightComptimeModel } from "services/models/corporate/insights/insightComptime.model";
import { ComptimeEmployeeHistoryModel } from "services/models/corporate/insights/comptimeEmployeHistory.model";
import { ComptimeEmployeeBalanceModel } from "services/models/corporate/insights/comptime-employee-balance.model";
import { ComptimeTacticalOccurrencesResponseModel, ComptimeTacticalResponseModel } from "services/models/corporate/insights/comptime-tactical.model";
import { ComptimeTacticalScreenConfig } from "services/models/corporate/insights/comptime-tactical-config.model";
import {
  AbsenteismTacticalResponseModel,
  normalizeAbsenteismCompanyTotals,
  normalizeAbsenteismInconsistencies,
} from "services/models/corporate/insights/absenteism-tactical.model";
import { InsightsTacticalLayoutKey } from "services/models/corporate/insights/absenteism-tactical-config.model";
import { InsightSesmtModel } from "services/models/corporate/insights/insightSesmt.model";
import { SesmtEmployeeHistoryModel } from "services/models/corporate/insights/sesmtEmployeHistory.model";
import { ConsolidatedInsightsDto } from "services/models/corporate/insights/consolidatedInsights.model";
import { FileService } from "@core/services/file.service";

@Injectable({ providedIn: 'root' })

export class InsightService {
  private endpoint = 'Insights';
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpService, private file: FileService) { }

  getSummary(params: URLSearchParams = null): Observable<Response<InsightSummaryModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';

    return this.http
      .get(`${this.endpoint}/summary${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightSummaryModel>();
          response.data = InsightSummaryModel.create(res.body);
          return response;
        })
      );
  }

  getHeadCount(params: URLSearchParams = null): Observable<Response<InsightHeadcountModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';

    return this.http
      .get(`${this.endpoint}/headcount${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightHeadcountModel>();
          response.data = InsightHeadcountModel.create(res.body);
          return response;
        })
      );
  }

  getTurnOver(params: URLSearchParams = null): Observable<Response<InsightTurnOverModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';

    return this.http
      .get(`${this.endpoint}/turnover${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightTurnOverModel>();
          response.data = InsightTurnOverModel.create(res.body);
          return response;
        })
      );
  }

  getAbsenteism(params: URLSearchParams = null): Observable<Response<InsightAbsenteismModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';

    return this.http
      .get(`${this.endpoint}/absenteism${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightAbsenteismModel>();
          response.data = InsightAbsenteismModel.create(res.body);
          return response;
        })
      );
  }

  getVacation(id: any): Observable<Response<InsightVacationModel>> {
    return this.http
      .get(`${this.endpoint}/vacation/${id}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightVacationModel>();
          response.data = InsightVacationModel.create(res.body);
          return response;
        })
      );
  }

  getOvertime(params: URLSearchParams = null): Observable<Response<InsightOvertimeModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/overtime${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightOvertimeModel>();
          response.data = InsightOvertimeModel.create(res.body);
          return response;
        })
      );
  }

  getComptime(params: URLSearchParams = null): Observable<Response<InsightComptimeModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/comptime${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightComptimeModel>();
          response.data = InsightComptimeModel.create(res.body);
          return response;
        })
      );
  }

  getComptimeTactical(params: URLSearchParams = null): Observable<Response<ComptimeTacticalResponseModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/comptime-tactical${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<ComptimeTacticalResponseModel>();
          response.data = (res.body ?? {}) as ComptimeTacticalResponseModel;
          return response;
        })
      );
  }

  getComptimeTacticalOccurrences(
    params: URLSearchParams = null
  ): Observable<Response<ComptimeTacticalOccurrencesResponseModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : "";
    return this.http
      .get(`${this.endpoint}/comptime-tactical/occurrences${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<ComptimeTacticalOccurrencesResponseModel>();
          response.data = (res.body ?? {}) as ComptimeTacticalOccurrencesResponseModel;
          return response;
        })
      );
  }

  getTacticalLayout<T extends { configVersion?: string }>(
    key: InsightsTacticalLayoutKey
  ): Observable<Response<T | null>> {
    return this.http
      .get(`${this.endpoint}/tactical/layout?key=${key}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<T | null>();
          response.data = InsightService.parseTacticalLayoutBody<T>(res.body);
          return response;
        })
      );
  }

  private static parseTacticalLayoutBody<T extends { configVersion?: string }>(body: unknown): T | null {
    if (!body || typeof body !== "object") {
      return null;
    }
    const record = body as Record<string, unknown>;
    const root = record as T;
    if (root?.configVersion) {
      return root;
    }

    const knownNestedKeys = ["AbsenteismTacticalConfig", "ComptimeTacticalConfig"];
    for (const key of knownNestedKeys) {
      const nested = record[key];
      if (InsightService.isTacticalLayoutConfig<T>(nested)) {
        return nested;
      }
    }

    for (const value of Object.values(record)) {
      if (InsightService.isTacticalLayoutConfig<T>(value)) {
        return value;
      }
    }

    return null;
  }

  private static isTacticalLayoutConfig<T extends { configVersion?: string }>(value: unknown): value is T {
    return !!value && typeof value === "object" && !!(value as T).configVersion;
  }

  getAbsenteismTactical(params: URLSearchParams = null): Observable<Response<AbsenteismTacticalResponseModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : "";
    return this.http
      .get(`${this.endpoint}/absenteism-tactical${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<AbsenteismTacticalResponseModel>();
          const body = res.body ?? {};
          const result = (body as Record<string, unknown>).result ?? body;
          const resultRecord = result as Record<string, unknown>;
          const employees = (resultRecord?.employees as AbsenteismTacticalResponseModel["employees"]) ?? [];
          const companyTotals = normalizeAbsenteismCompanyTotals(
            resultRecord?.companyTotals ?? resultRecord?.CompanyTotals
          );
          const inconsistencies = normalizeAbsenteismInconsistencies(
            resultRecord?.inconsistencies ?? resultRecord?.Inconsistencies
          );
          const rawPercent = resultRecord?.currentPercent;
          const currentPercent =
            typeof rawPercent === "number" && !Number.isNaN(rawPercent) ? rawPercent : 0;
          response.data = { employees, companyTotals, currentPercent, inconsistencies };
          return response;
        })
      );
  }

  exportComptimeEvents(params: URLSearchParams = null) {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/export-comptime-events${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res: any) => {
          const blobData = res.body.fileContents;
          const blob = this.file.base64ToBlob(blobData);
          const type = 'csv';
          this.file.saveFile(blob, `${res.body.fileDownloadName}.${type}`);
          return blob;
        }),
        timeout(60000)
      );
  }

  getSesmt(params: URLSearchParams = null): Observable<Response<InsightSesmtModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/sesmt${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<InsightSesmtModel>();
          response.data = InsightSesmtModel.create(res.body);
          return response;
        })
      );
  }

  getComptimeEmployeeBalance(employeeId: string): Observable<Response<ComptimeEmployeeBalanceModel>> {
    return this.http
      .get(
        `${this.endpoint}/comptime-employee-balance/${employeeId}`,
        undefined,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res) => {
          const response = new Response<ComptimeEmployeeBalanceModel>();
          response.data = ComptimeEmployeeBalanceModel.create(res.body);
          return response;
        })
      );
  }

  getComptimeEmployeeHistory(params: URLSearchParams = null): Observable<Response<ComptimeEmployeeHistoryModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/comptime-employee-history${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<ComptimeEmployeeHistoryModel>();
          response.data = ComptimeEmployeeHistoryModel.create(res.body);
          return response;
        })
      );
  }

  getSesmtEmployeeHistory(params: URLSearchParams = null): Observable<Response<SesmtEmployeeHistoryModel>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';
    return this.http
      .get(`${this.endpoint}/sesmt-employee-history${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<SesmtEmployeeHistoryModel>();
          response.data = SesmtEmployeeHistoryModel.create(res.body);
          return response;
        })
      );
  }

  getConsolidatedInsights(
    params: URLSearchParams = null
  ): Observable<Response<ConsolidatedInsightsDto>> {
    const queryParams = params !== null ? `?${params.toString()}` : '';

    return this.http
      .get(`${this.endpoint}/consolidated${queryParams}`, undefined, this.baseUrl, null, true)
      .pipe(
        map((res) => {
          const response = new Response<ConsolidatedInsightsDto>();
          const body = res.body ?? {};
          response.data = (body.data != null ? body.data : body) as ConsolidatedInsightsDto;
          return response;
        })
      );
  }
}